import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

const DROPDOWN_HEIGHT = 300;
const Z_INDEX = 2147483647;

const TableHeaderFilter = ({ label, options, selectedValues, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let width = Math.max(rect.width, 220);
    let left = rect.left;

    // prevent right overflow
    if (left + width > viewportWidth - 8) {
      left = viewportWidth - width - 8;
    }
    // prevent left overflow
    if (left < 8) left = 8;

    // decide open direction: below if enough space, else above
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < DROPDOWN_HEIGHT + 8 && spaceAbove > spaceBelow;

    const top = openUpward
      ? rect.top + window.scrollY - DROPDOWN_HEIGHT - 2   // attach to top edge of header
      : rect.bottom + window.scrollY + 2;                 // attach to bottom edge of header

    setCoords({ top, left: left + window.scrollX, width, openUpward });
  };

  const toggleDropdown = () => {
    if (!open) updatePosition();
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filteredOptions = useMemo(() => {
    return options.filter((opt) =>
      String(opt?.label ?? "").toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const handleSelect = (val) => {
    const next = selectedValues.includes(val)
      ? selectedValues.filter((v) => v !== val)
      : [...selectedValues, val];
    onChange(next);
  };

  const dropdown = (
    <div
      ref={dropdownRef}
      style={{
        position: "absolute",
        top: coords.top,
        left: coords.left,
        width: coords.width,
        height: DROPDOWN_HEIGHT,
        zIndex: Z_INDEX,
      }}
      className="bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col overflow-hidden"
    >
      {/* Search */}
      <div className="p-2 border-b bg-gray-50 shrink-0">
        <input
          autoFocus
          className="w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          placeholder={`Search ${label}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Options */}
      <div className="flex-1 overflow-y-auto">
        {filteredOptions.length ? (
          filteredOptions.map((opt) => (
            <div
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm select-none
                ${selectedValues.includes(opt.value)
                  ? "bg-indigo-50 text-indigo-700 font-semibold"
                  : "text-gray-700 hover:bg-violet-50"
                }`}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(opt.value)}
                readOnly
                className="accent-indigo-600"
              />
              <span className="truncate">{opt.label}</span>
            </div>
          ))
        ) : (
          <div className="p-4 text-center text-gray-400 text-xs">No results</div>
        )}
      </div>

      {/* Footer */}
      <div className="p-2 border-t bg-gray-50 flex justify-between shrink-0">
        <button
          onClick={() => onChange([])}
          className="text-xs text-red-500 font-semibold hover:text-red-700 transition-colors"
        >
          Clear
        </button>
        <button
          onClick={() => setOpen(false)}
          className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 transition-colors"
        >
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div
        ref={triggerRef}
        onClick={toggleDropdown}
        className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded transition-colors select-none
          ${selectedValues.length
            ? "text-orange-300 bg-orange-900/40"
            : "text-white hover:bg-orange-100"
          }`}
      >
        <span className="font-bold text-[13px] text-slate-500">{label}</span>
        <span
          className={`text-[9px] transition-transform duration-200 text-violet-300 ${open ? "rotate-180" : ""}`}
        >
          ▼
        </span>
        {selectedValues.length > 0 && (
          <span className="bg-orange-400 text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center font-bold">
            {selectedValues.length}
          </span>
        )}
      </div>

      {open && createPortal(dropdown, document.body)}
    </>
  );
};

export default TableHeaderFilter;